import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Row,
  Col,
  Switch,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../redux/selector';
import { post, get } from '../../../../services/ApiService';

const { TextArea } = Input;

// Constants

const VALID_COMPANY_LOCATIONS = [
  { value: 'MI Health Longcroft House', label: 'MI Health Longcroft House' },
  { value: 'Berkeley Care Unit', label: 'Berkeley Care Unit' },
];

const reasonOptions = [
  { label: 'Royal Mail Lost In transit', value: 'Royal Mail Lost In transit', requireOrder: true },
  { label: 'Amazon Shipping Lost In transit', value: 'Amazon Shipping Lost In transit', requireOrder: true },
  { label: 'Royal Mail Delivered, Cannot be located', value: 'Royal Mail Delivered, Cannot be located', requireOrder: true },
  { label: 'Amazon Shipping, Cannot be located', value: 'Amazon Shipping, Cannot be located', requireOrder: true },
  { label: 'Royal Mail Damaged Delivery', value: 'Royal Mail Damaged Delivery', requireOrder: true },
  { label: 'Amazon Shipping Damaged Delivery', value: 'Amazon Shipping Damaged Delivery', requireOrder: true },
  { label: 'Pharmacy Fulfilment Error', value: 'Pharmacy Fulfilment Error', requireOrder: true },
  { label: 'Disposal Expired', value: 'Disposal Expired', requireOrder: false },
  { label: 'Disposal Damage', value: 'Disposal Damage', requireOrder: false },
  { label: 'Customer Preference / Change', value: 'Customer Preference / Change', requireOrder: false },
  { label: 'Other - Please add comments', value: 'Other - Please add comments', requireOrder: false },
];

const ERROR_MESSAGES = {
  REPLACEMENT_ALREADY_EXISTS: 'A replacement has already been issued for this order.',
  ROYAL_MAIL_REJECTED: 'Royal Mail rejected the shipment. Please check the order details.',
  ROYAL_MAIL_ERROR: 'Could not reach Royal Mail. Please try again shortly.',
  ROYAL_MAIL_TIMEOUT: 'Royal Mail request timed out. Please try again.',
  PRODUCT_ENRICHMENT_FAILED: null,
  PAYLOAD_BUILD_FAILED: null,
  DB_WRITE_FAILED: null,
  INVALID_PAYLOAD: null,
  INVALID_ITEMS: 'Each item must have a quantity of at least 1.',
  INVALID_TRADING_COMPANY: 'Please select a valid trading company.',
};

// Helpers

function openBase64Pdf(base64) {
  try {
    const byteChars = atob(base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch {
    message.warning('Label was generated but could not be opened. Please print from Click & Drop.');
  }
}

/** Build a map from each SKU string → its ppid, using the products catalog. */
function buildSkuToPpid(products) {
  const map = {};
  for (const p of products) {
    for (const sku of p.skus || []) {
      map[sku] = p.id;
    }
  }
  return map;
}

function resolveSku(product, orderSku) {
  const skus = product?.skus || [];
  return skus.includes(orderSku) ? orderSku : skus[0] || '';
}
function packSizeFor(product, orderSku) {
  const sku = resolveSku(product, orderSku);
  const skuQty = product?.skuQuantities?.[sku];
  return Number.isInteger(skuQty) && skuQty >= 1 ? skuQty : null;
}
function defaultUnitsFor(product, orderSku, orderQuantity) {
  const skuQty = packSizeFor(product, orderSku);
  if (skuQty == null) return null;
  if (!(Number.isInteger(orderQuantity) && orderQuantity >= 1)) return null;
  return skuQty * orderQuantity;
}

// Component

const ReplacementModal = ({ open, onCancel, onSuccess, order }) => {
  const user = useSelector(selectUser);
  const [products, setProducts] = useState([]);
  const [tradingCompanies, setTradingCompanies] = useState([]);
  const [itemPpid, setItemPpid] = useState({});
  const [itemQuantities, setItemQuantities] = useState({});
  const [date, setDate] = useState(dayjs());
  const [tradingId, setTradingId] = useState(null);
  const [companyLocation, setCompanyLocation] = useState(null);
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState('');
  const [countAsStockLoss, setCountAsStockLoss] = useState(true);
  const [noStockLossReasonChoice, setNoStockLossReasonChoice] = useState(null);
  const [noStockLossReasonOther, setNoStockLossReasonOther] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProducts([]);
    setTradingCompanies([]);
    setItemPpid({});
    setItemQuantities({});
    setDate(dayjs());
    setTradingId(null);
    setCompanyLocation(null);
    setReason(null);
    setNotes('');
    setCountAsStockLoss(true);
    setNoStockLossReasonChoice(null);
    setNoStockLossReasonOther('');

    // Pre-fill quantities from order
    const initialQuantities = {};
    (order?.products || []).forEach((p) => {
      initialQuantities[p.sku] = p.quantity;
    });
    setItemQuantities(initialQuantities);

    const fetchProducts = async () => {
      try {
        const response = await get('api/products/ppid');
        if (cancelled) return;
        if (response?.success && Array.isArray(response.products)) {
          setProducts(response.products);
          const skuToPpid = buildSkuToPpid(response.products);
          const productById = new Map(response.products.map((p) => [p.id, p]));
          const initialPpids = {};
          const defaultQuantities = {};
          (order?.products || []).forEach((p) => {
            const ppid = skuToPpid[p.sku] ?? null;
            initialPpids[p.sku] = ppid;
            const units = defaultUnitsFor(productById.get(ppid), p.sku, p.quantity);
            defaultQuantities[p.sku] = units ?? p.quantity;
          });
          setItemPpid(initialPpids);
          setItemQuantities(defaultQuantities);
        } else {
          if (!cancelled) message.error('Failed to load product catalog');
        }
      } catch {
        if (!cancelled) message.error('Failed to load product catalog');
      }
    };

    const fetchTradingCompanies = async () => {
      try {
        const response = await get('api/trading-companies');
        if (cancelled) return;
        if (response?.success && Array.isArray(response.tradingCompanies)) {
          setTradingCompanies(response.tradingCompanies);

          const matched = order?.fulfilmentBy
            ? response.tradingCompanies.find(
                (tc) => tc.trading_name === order.fulfilmentBy
              )
            : null;
          if (matched) {
            setTradingId(matched.id);
            const locMatch = VALID_COMPANY_LOCATIONS.find(
              (loc) => loc.value === matched.company_name
            );
            if (locMatch) {
              setCompanyLocation(locMatch.value);
            }
          }
          if (
            order?.location &&
            VALID_COMPANY_LOCATIONS.some((loc) => loc.value === order.location)
          ) {
            setCompanyLocation((current) => current ?? order.location);
          }
        } else {
          if (!cancelled) message.error('Failed to load trading companies');
        }
      } catch {
        if (!cancelled) message.error('Failed to load trading companies');
      }
    };

    fetchProducts();
    fetchTradingCompanies();

    return () => {
      cancelled = true;
    };
  }, [open, order?._id]);

  const handleTradingChange = (newTradingId) => {
    setTradingId(newTradingId);
    if (!newTradingId) return;
    const selected = tradingCompanies.find((tc) => tc.id === newTradingId);
    if (!selected) return;
    const match = VALID_COMPANY_LOCATIONS.find(
      (loc) => loc.value === selected.company_name
    );
    if (match) setCompanyLocation(match.value);
  };

  const findProduct = (ppid) => products.find((p) => p.id === ppid) || null;
  const rowHasNoSkus = (orderSku) => {
    const ppid = itemPpid[orderSku];
    if (!ppid) return false; // no selection yet — caught by "empty ppid" check
    const prod = findProduct(ppid);
    return prod ? (prod.skus || []).length === 0 : false;
  };

  const orderItems = order?.products || [];

  const OTHER_REASON_VALUE = 'Other - Please add comments';

  const resolvedNoStockLossReason = !countAsStockLoss
    ? noStockLossReasonChoice === OTHER_REASON_VALUE
      ? noStockLossReasonOther.trim()
      : noStockLossReasonChoice
    : null;

  const isNoStockLossReasonInvalid =
    !countAsStockLoss &&
    (!noStockLossReasonChoice ||
      (noStockLossReasonChoice === OTHER_REASON_VALUE &&
        noStockLossReasonOther.trim().length === 0));

  const isSubmitDisabled =
    loading ||
    !reason ||
    !companyLocation ||
    !tradingId ||
    !date ||
    isNoStockLossReasonInvalid ||
    orderItems.some((p) => !itemPpid[p.sku]) ||
    orderItems.some((p) => {
      const q = itemQuantities[p.sku];
      return !Number.isInteger(q) || q < 1;
    }) ||
    orderItems.some((p) => rowHasNoSkus(p.sku));

  // Submit
  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    const items = orderItems.map((p) => {
      const ppid = itemPpid[p.sku];
      const prod = findProduct(ppid);
      const prodSkus = prod?.skus || [];
      const sku = prodSkus.includes(p.sku) ? p.sku : prodSkus[0] || '';
      return {
        ppid,
        sku,
        quantity: itemQuantities[p.sku],
      };
    });

    setLoading(true);
    try {
      const response = await post('api/replacements/create', {
        orderId: order._id,
        reason,
        notes,
        companyLocation,
        tradingId,
        date: date.toISOString(),
        items,
        countAsStockLoss,
        noStockLossReason: resolvedNoStockLossReason,
        actorUserId: user?._id || '',
        actorName:
          `${user?.firstname || ''} ${user?.lastname || ''}`.trim() ||
          user?.email ||
          'Unknown',
        actorRole: user?.role?.name || 'unknown',
      });

      if (response.success) {
        onSuccess(response);
        onCancel();
        if (response.labelBase64) {
          message.success(
            'Replacement submitted. Packing slip and label generated — opening for review...'
          );
          openBase64Pdf(response.labelBase64);
        } else {
          message.success(
            'Replacement submitted. Label and packing slip saved — available for the pharmacy team to print.'
          );
        }
        if (Array.isArray(response.warnings) && response.warnings.length > 0) {
          response.warnings.forEach((w) => message.warning(w.message, 8));
        }
      } else {
        const mapped = ERROR_MESSAGES[response.code];
        const userMessage =
          mapped !== undefined
            ? mapped || response.message
            : response.message || 'Failed to create replacement';
        message.error(userMessage);
      }
    } catch {
      message.error('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render
  const labelStyle = { fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 6 };
  const fieldWrap = { marginBottom: 20 };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      maskClosable={!loading}
      closable={!loading}
      title={
        <span>
          <i className="fas fa-redo me-2 text-warning" />
          Issue Replacement Order
        </span>
      }
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger
          loading={loading}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {loading ? 'Submitting to Royal Mail...' : 'Confirm Replacement'}
        </Button>,
      ]}
      width={560}
      destroyOnHidden
    >
      <div style={{ padding: '8px 0' }}>

        {/* Per-SKU blocks */}
        {orderItems.map((item, idx) => (
          <div key={item.sku}>
            <Row gutter={16}>
              <Col span={24}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>
                    Product <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select a product"
                    value={itemPpid[item.sku] ?? undefined}
                    onChange={(val) => {
                      setItemPpid((prev) => ({ ...prev, [item.sku]: val }));
                      const units = defaultUnitsFor(findProduct(val), item.sku, item.quantity);
                      if (units != null) {
                        setItemQuantities((prev) => ({ ...prev, [item.sku]: units }));
                      }
                    }}
                    style={{ width: '100%' }}
                  >
                    {products.map((p) => (
                      <Select.Option key={p.id} value={p.id}>
                        pp{p.id} - {p.product}
                      </Select.Option>
                    ))}
                  </Select>
                  {rowHasNoSkus(item.sku) && (
                    <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                      Selected product has no SKU mapping — cannot ship.
                    </span>
                  )}
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>
                    Quantity <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <InputNumber
                    min={1}
                    value={itemQuantities[item.sku] ?? item.quantity}
                    onChange={(val) =>
                      setItemQuantities((prev) => ({
                        ...prev,
                        [item.sku]: Number.isInteger(val) && val >= 1 ? val : prev[item.sku],
                      }))
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              </Col>
              {idx === 0 && (
                <Col span={12}>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>
                      Date <span style={{ color: '#ff4d4f' }}>*</span>
                    </label>
                    <DatePicker
                      value={date}
                      onChange={setDate}
                      style={{ width: '100%' }}
                      format="MMM DD, YYYY"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </div>
        ))}

        {/* Global fields */}
        <Row gutter={16}>
          <Col span={12}>
            <div style={fieldWrap}>
              <label style={labelStyle}>
                Trading Company <span style={{ color: '#ff4d4f' }}>*</span>
              </label>
              <Select
                showSearch
                optionFilterProp="children"
                allowClear
                placeholder="Select trading company"
                value={tradingId ?? undefined}
                onChange={handleTradingChange}
                style={{ width: '100%' }}
              >
                {tradingCompanies.map((tc) => (
                  <Select.Option key={tc.id} value={tc.id}>
                    {tc.trading_name}{tc.company_name ? ` - ${tc.company_name}` : ''}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div style={fieldWrap}>
              <label style={labelStyle}>
                Company Location <span style={{ color: '#ff4d4f' }}>*</span>
              </label>
              <Select
                allowClear
                placeholder="Select company location"
                value={companyLocation ?? undefined}
                onChange={setCompanyLocation}
                style={{ width: '100%' }}
              >
                {VALID_COMPANY_LOCATIONS.map((loc) => (
                  <Select.Option key={loc.value} value={loc.value}>
                    {loc.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>

        <div style={fieldWrap}>
          <label style={labelStyle}>
            Reason <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Select
            placeholder="Select a reason"
            value={reason}
            onChange={setReason}
            style={{ width: '100%' }}
          >
            {reasonOptions.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>
            Order ID
          </label>
          <Input value={order?.orderId || ''} readOnly />
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>
            Notes <span style={{ color: '#8c8c8c', fontWeight: 400 }}>(optional)</span>
          </label>
          <TextArea
            rows={3}
            placeholder="Add any additional context..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            showCount
          />
        </div>

        <div
          style={{
            padding: '12px 14px',
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            background: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Switch
              checked={countAsStockLoss}
              onChange={(checked) => {
                setCountAsStockLoss(checked);
                if (checked) {
                  setNoStockLossReasonChoice(null);
                  setNoStockLossReasonOther('');
                }
              }}
              disabled={loading}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Count as Stock Loss
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                {countAsStockLoss
                  ? 'Inventory will be deducted and a stock-loss record will be created.'
                  : 'Inventory will NOT be deducted and no stock-loss record will be created.'}
              </div>
            </div>
          </div>

          {!countAsStockLoss && (
            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>
                Reason for not counting as stock loss{' '}
                <span style={{ color: '#ff4d4f' }}>*</span>
              </label>
              <Select
                placeholder="Select a reason"
                value={noStockLossReasonChoice}
                onChange={(val) => {
                  setNoStockLossReasonChoice(val);
                  if (val !== OTHER_REASON_VALUE) setNoStockLossReasonOther('');
                }}
                style={{ width: '100%' }}
                disabled={loading}
              >
                {reasonOptions.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>

              {noStockLossReasonChoice === OTHER_REASON_VALUE && (
                <div style={{ marginTop: 10 }}>
                  <label style={labelStyle}>
                    Please specify <span style={{ color: '#ff4d4f' }}>*</span>
                  </label>
                  <TextArea
                    rows={3}
                    placeholder="Describe the reason for not counting as stock loss..."
                    value={noStockLossReasonOther}
                    onChange={(e) => setNoStockLossReasonOther(e.target.value)}
                    maxLength={500}
                    showCount
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
};

ReplacementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
};

export default ReplacementModal;
